import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { syncCcyFromUser } from "../currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "imperio_token";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  useEffect(() => { if (user) syncCcyFromUser(user); }, [user]);

  // Single-device session: when the backend reports this token was superseded by a login on
  // another device, force a clean logout with a friendly message and send the user to login.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const detail = error?.response?.data?.detail;
        if (error?.response?.status === 401 && typeof detail === "string" && detail.includes("another device")) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
          toast.error(detail);
          if (!window.location.pathname.startsWith("/login")) {
            window.location.href = "/login?reason=another_device";
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const saveSession = (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.user);
  };

  const refreshUser = async () => {
    const tk = localStorage.getItem(TOKEN_KEY);
    if (!tk) return;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${tk}` } });
      setUser(res.data);
      return res.data;
    } catch (e) { /* keep existing user on transient error */ }
  };

  const login = async (email, password, totp_code, captcha_token) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, ...(totp_code ? { totp_code } : {}), ...(captcha_token ? { captcha_token } : {}) });
    // May return { requires_2fa } (needs TOTP) or { requires_otp } (email code) — no session yet.
    if (res.data.requires_2fa || res.data.requires_otp) return res.data;
    saveSession(res.data);
    return res.data;
  };

  const verifyLoginOtp = async (email, code) => {
    const res = await axios.post(`${API}/auth/login/verify-otp`, { email, code });
    saveSession(res.data);
    return res.data;
  };

  const register = async (full_name, email, password, ref_code, extra = {}) => {
    // Backend now returns { requires_verification: true } and NO session — user must verify email first.
    const res = await axios.post(`${API}/auth/register`, { full_name, email, password, ...(ref_code ? { ref_code } : {}), ...extra });
    if (res.data.access_token) saveSession(res.data);
    return res.data;
  };

  const verifyEmail = async (verifyToken) => {
    const res = await axios.post(`${API}/auth/verify-email`, { token: verifyToken });
    if (res.data.access_token) saveSession(res.data);
    return res.data;
  };

  const resendVerification = async (email) => {
    const res = await axios.post(`${API}/auth/resend-verification`, { email });
    return res.data;
  };

  const forgotPassword = async (email) => {
    const res = await axios.post(`${API}/auth/forgot-password`, { email });
    return res.data;
  };

  const resetPassword = async (email, code, new_password) => {
    const res = await axios.post(`${API}/auth/reset-password`, { email, code, new_password });
    return res.data;
  };

  const loginWithGoogle = async (code) => {
    const res = await axios.post(`${API}/auth/google`, { code });
    saveSession(res.data);
    return res.data;
  };

  const loginWithApple = async (code, state, name) => {
    const res = await axios.post(`${API}/auth/apple`, { code, state, name }, { withCredentials: true });
    saveSession(res.data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifyLoginOtp, loginWithGoogle, loginWithApple, register, verifyEmail, resendVerification, forgotPassword, resetPassword, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
